import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { serverTimestamp } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import "./styles.css";
import "./auth-management.css";

const SUBSCRIPTION_PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    period: "forever",
    icon: "🆓",
    features: [
      "View all RFQs",
      "Read-only access",
      "Basic dashboard",
      "Email support",
    ],
    limitations: [
      "No editing capabilities",
      "No AI features",
      "No proposal writer",
      "No vendor management",
      "No email automation",
    ],
  },
  {
    id: "pro",
    name: "Pro Access",
    price: 100,
    period: "per login",
    icon: "⭐",
    features: [
      "Full app access",
      "Create & edit RFQs",
      "AI-powered proposals",
      "Vendor management",
      "Email automation",
      "Quote comparison",
      "Real-time tracking",
      "Compliance tracking",
      "Priority support",
    ],
    limitations: [],
    popular: true,
  },
];

export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const { userProfile, updateUserProfile } = useAuth();
  
  const [currentPlan, setCurrentPlan] = useState("free");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (userProfile?.subscription) {
      setCurrentPlan(userProfile.subscription);
    }
  }, [userProfile]);

  // Handle plan selection
  const handleSelectPlan = (planId) => {
    if (planId === currentPlan) return;
    setSelectedPlan(planId);
    setShowConfirmModal(true);
  };

  // Confirm plan change
  const handleConfirmPlanChange = async () => {
    setLoading(true);
    
    try {
      // Update user subscription in Firestore
      const result = await updateUserProfile({
        subscription: selectedPlan,
        subscriptionUpdatedAt: serverTimestamp(),
      });

      if (result.success) {
        setCurrentPlan(selectedPlan);
        setStatusMessage({ 
          type: "success", 
          text: `Successfully ${selectedPlan === "free" ? "downgraded" : "upgraded"} to ${SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.name} plan!` 
        });
      } else {
        setStatusMessage({ type: "error", text: result.error });
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      setStatusMessage({ type: "error", text: "Failed to update subscription" });
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setSelectedPlan(null);
    }
  };

  // Get plan details
  const getPlanDetails = (planId) => {
    return SUBSCRIPTION_PLANS.find(p => p.id === planId);
  };

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <h1>💳 Subscription Management</h1>
        <p>Choose the plan that's right for you</p>
      </div>

      {statusMessage.text && (
        <div className={`status-message status-${statusMessage.type}`}>
          {statusMessage.type === "success" ? "✅" : "❌"} {statusMessage.text}
        </div>
      )}

      {/* Current Plan Banner */}
      <div className="current-plan-banner">
        <div className="current-plan-info">
          <span className="current-plan-icon">{getPlanDetails(currentPlan)?.icon}</span>
          <div>
            <h3>Current Plan: {getPlanDetails(currentPlan)?.name}</h3>
            <p>
              {currentPlan === "free" 
                ? "Upgrade to unlock more features" 
                : `$${getPlanDetails(currentPlan)?.price}/${getPlanDetails(currentPlan)?.period}`
              }
            </p>
          </div>
        </div>
        {userProfile?.subscriptionUpdatedAt && (
          <div className="plan-since">
            Member since {userProfile.subscriptionUpdatedAt?.toDate?.()?.toLocaleDateString() || "N/A"}
          </div>
        )}
      </div>

      {/* Subscription Plans Grid */}
      <div className="subscription-plans-grid">
        {SUBSCRIPTION_PLANS.map((plan) => (
          <div 
            key={plan.id} 
            className={`subscription-card ${plan.id === currentPlan ? "current" : ""} ${plan.popular ? "popular" : ""}`}
          >
            {plan.popular && <div className="popular-badge">Most Popular</div>}
            {plan.id === currentPlan && <div className="current-badge">Current Plan</div>}
            
            <div className="plan-header">
              <span className="plan-icon">{plan.icon}</span>
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                {plan.price === 0 ? (
                  <span className="price-free">Free</span>
                ) : (
                  <>
                    <span className="price-currency">$</span>
                    <span className="price-amount">{plan.price}</span>
                    <span className="price-period">/{plan.period}</span>
                  </>
                )}
              </div>
            </div>

            <div className="plan-features">
              <h4>Features included:</h4>
              <ul className="features-list">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    <span className="feature-check">✅</span>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {plan.limitations.length > 0 && (
                <>
                  <h4>Not included:</h4>
                  <ul className="limitations-list">
                    {plan.limitations.map((limitation, idx) => (
                      <li key={idx} className="limitation-item">
                        <span className="feature-x">❌</span>
                        {limitation}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <button
              className={`plan-btn ${plan.id === currentPlan ? "current-btn" : plan.popular ? "popular-btn" : ""}`}
              onClick={() => handleSelectPlan(plan.id)}
              disabled={plan.id === currentPlan || loading}
            >
              {plan.id === currentPlan 
                ? "Current Plan" 
                : plan.price > getPlanDetails(currentPlan)?.price 
                  ? "Upgrade" 
                  : "Downgrade"
              }
            </button>
          </div>
        ))}
      </div>

      {/* Feature Comparison Table */}
      <div className="feature-comparison">
        <h2>Feature Comparison</h2>
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Free</th>
              <th>Pro Access</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>View RFQs</td>
              <td>✅</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Create & Edit RFQs</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>AI Features</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Proposal Writer</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Vendor Management</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Email Automation</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Quote Comparison</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Compliance Tracking</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Support</td>
              <td>Email</td>
              <td>Priority Email</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedPlan && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirm Plan Change</h2>
            <p>
              You are about to {getPlanDetails(selectedPlan)?.price > getPlanDetails(currentPlan)?.price ? "upgrade" : "downgrade"} from{" "}
              <strong>{getPlanDetails(currentPlan)?.name}</strong> to{" "}
              <strong>{getPlanDetails(selectedPlan)?.name}</strong>.
            </p>
            
            {selectedPlan !== "free" && (
              <div className="price-change-info">
                <p>
                  New monthly cost: <strong>${getPlanDetails(selectedPlan)?.price}</strong>
                </p>
              </div>
            )}
            
            {getPlanDetails(selectedPlan)?.price < getPlanDetails(currentPlan)?.price && (
              <div className="downgrade-warning">
                ⚠️ Downgrading may result in loss of access to some features. Make sure to export any data you need before proceeding.
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedPlan(null);
                }}
                style={{ flex: 1 }}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="new-btn" 
                onClick={handleConfirmPlanChange}
                style={{ flex: 1 }}
                disabled={loading}
              >
                {loading ? "Processing..." : "Confirm Change"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
